import React, { useRef, useState } from "react";
import { ChevronsRight, Check } from "lucide-react";

const TONES = {
  gold: { fill: "bg-gold", knob: "bg-gold text-black", text: "text-gold", border: "border-gold/40" },
  sky: { fill: "bg-sky-500", knob: "bg-sky-500 text-black", text: "text-sky-400", border: "border-sky-400/40" },
  emerald: { fill: "bg-emerald-500", knob: "bg-emerald-500 text-black", text: "text-emerald-400", border: "border-emerald-400/40" },
};
const KNOB = 52;

// Uber-style slide-to-confirm control. Drag the knob to the end to fire onConfirm.
export function SlideToConfirm({ label, onConfirm, tone = "gold", testId = "slide" }) {
  const trackRef = useRef(null);
  const xRef = useRef(0);
  const dragging = useRef(false);
  const [x, setX] = useState(0);
  const [done, setDone] = useState(false);
  const t = TONES[tone] || TONES.gold;

  const maxX = () => (trackRef.current ? trackRef.current.offsetWidth - KNOB - 4 : 0);
  const setPos = (v) => { xRef.current = v; setX(v); };

  const move = (clientX) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(clientX - rect.left - KNOB / 2, maxX())));
  };
  const end = () => {
    dragging.current = false;
    if (xRef.current >= maxX() - 8) {
      setDone(true);
      setPos(maxX());
      onConfirm && onConfirm();
      setTimeout(() => { setDone(false); setPos(0); }, 600);
    } else {
      setPos(0);
    }
  };

  return (
    <div
      ref={trackRef}
      className={`relative w-full h-14 border ${t.border} bg-[var(--nl-bg-3)] overflow-hidden select-none`}
      data-testid={testId}
    >
      <div className={`absolute inset-y-0 left-0 ${t.fill} opacity-25 transition-[width] duration-150`} style={{ width: `${x + KNOB}px` }} />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className={`label text-[13px] font-bold tracking-wide ${done ? "text-white" : t.text}`} style={{ opacity: Math.max(0.25, 1 - x / (maxX() || 1)) }}>
          {done ? "Confirmed" : label}
        </span>
      </div>
      <div
        role="button"
        aria-label={label}
        onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { if (dragging.current) move(e.clientX); }}
        onPointerUp={() => { if (dragging.current) end(); }}
        onPointerCancel={() => { if (dragging.current) end(); }}
        className={`absolute top-1 left-1 h-12 w-12 flex items-center justify-center ${t.knob} cursor-grab active:cursor-grabbing touch-none shadow-lg`}
        style={{ transform: `translateX(${x}px)`, transition: dragging.current ? "none" : "transform 0.25s ease" }}
        data-testid={`${testId}-knob`}
      >
        {done ? <Check size={22} /> : <ChevronsRight size={22} />}
      </div>
    </div>
  );
}
